import { getApi } from "../Services/apiService";

/**
 * The "In-Law Birthdays" report — GET /report-management/in-law-birthdays.
 *
 * The endpoint does the whole report: living, still-married in-laws only (no
 * deceased spouses, no divorced ones, nobody without a birth date), grouped
 * into the twelve months and sorted day-ascending then A–Z inside each. Every
 * row carries its birth day, name parts and the family-line colour inherited
 * from the blood family member it is married to (`spouse_of`).
 *
 * `month` narrows the payload to one month (1–12); `year` only moves the
 * weekday each birthday falls on, since a birthday comes round every year.
 * Both the dashboard card's count and the detail page's list come through
 * here, so the two can never show different numbers.
 *
 * Sample month:
 *   { month: 1, month_name: "January", count: 4, members: [
 *       { id, day: 6, name: "Oscar Plant", first_name, middle_name, last_name,
 *         suffix, maiden_name, nick_name, dob: "1980-01-06",
 *         formatted_dob: "01/06/1980", color_name: "Dk Green",
 *         family_line: "Dk Green", hex_code: "#013220", generation_level: 3,
 *         spouse_of: { id, name, first_name, last_name, color_name },
 *         is_spouse: true } ] }
 */

export const IN_LAW_BIRTHDAY_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Whole number or null — a day the payload does not name sorts last. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const EMPTY_REPORT = {
  title: "",
  year: null,
  grandTotal: 0,
  months: [],
  rows: [],
};

/**
 * The day of the month off a row: the endpoint's own `day`, else read from the
 * ISO `dob` it prints beside it. -1 where neither names one.
 */
function readBirthDay(member) {
  const fromDay = toNumberOrNull(member?.day);
  if (fromDay !== null) return fromDay;

  const isoMatch = String(member?.dob ?? "").match(/^\d{4}-(\d{2})-(\d{2})/);
  if (isoMatch) return parseInt(isoMatch[2], 10);

  const slashMatch = String(member?.formatted_dob ?? "").match(
    /^(\d{1,2})\/(\d{1,2})\/\d{4}/
  );
  if (slashMatch) return parseInt(slashMatch[2], 10);

  return -1;
}

/**
 * One API row → the row the page prints. The report row is re-spelled as the
 * in-law record the dashboard name helpers expect — the in-law flag set — so
 * its name prints the way Birthdays this Month prints an in-law: first, last
 * and suffix. `spouse_of` is the blood spouse, not the head of the line, so it
 * is deliberately not copied into the `head_*` fields the line column reads.
 */
function toReportRow(member, monthIndex, index) {
  const spouseOf =
    member?.spouse_of && typeof member.spouse_of === "object"
      ? member.spouse_of
      : null;

  return {
    id: String(member?.id ?? `in-law-${monthIndex}-${index}`),
    monthIndex,
    day: readBirthDay(member),
    colorName: String(member?.color_name ?? member?.family_line ?? "").trim(),
    hexCode: String(member?.hex_code ?? "").trim() || "#000000",
    spouseOfName: String(spouseOf?.name ?? "").trim(),
    member: {
      ...member,
      is_in_laws: true,
      color_name: member?.color_name ?? member?.family_line ?? "",
    },
  };
}

/**
 * The report for one family tree. `month` (1–12) narrows it to that month;
 * left out, all twelve come back. `year` sets the calendar year the weekdays
 * are read against. `grandTotal` is the API's own count of the rows returned,
 * which is what the heading and the dashboard card show.
 */
export async function fetchInLawBirthdays(familyTreeId, { year, month } = {}) {
  if (!familyTreeId) return EMPTY_REPORT;

  const params = new URLSearchParams({ family_tree_id: familyTreeId });
  if (year) params.set("year", String(year));
  if (month) params.set("month", String(month));

  const response = await getApi(
    `/report-management/in-law-birthdays?${params.toString()}`
  );

  if (response?.statusCode !== 200 || !response?.data) return EMPTY_REPORT;

  const reportData = Array.isArray(response.data.report_data)
    ? response.data.report_data
    : [];

  const months = reportData
    .map((group) => {
      const monthNumber = toNumberOrNull(group?.month);
      const monthIndex = monthNumber !== null ? monthNumber - 1 : -1;
      const members = Array.isArray(group?.members) ? group.members : [];
      return {
        month: monthNumber,
        monthName:
          String(group?.month_name ?? "").trim() ||
          IN_LAW_BIRTHDAY_MONTH_NAMES[monthIndex] ||
          "",
        count: toNumberOrNull(group?.count) ?? members.length,
        rows: members.map((member, index) => toReportRow(member, monthIndex, index)),
      };
    })
    // A month filter can still come back with the other eleven months as
    // empty groups; only the month asked for is kept.
    .filter((group) => !month || group.month === Number(month));

  const rows = months.flatMap((group) => group.rows);

  return {
    title: response.data.title || "",
    year: toNumberOrNull(response.data.year),
    grandTotal: month
      ? rows.length
      : toNumberOrNull(response.data.grand_total) ?? rows.length,
    months,
    rows,
  };
}
